<template>
  <div class="box">
    <h3>Ad Creative</h3>

    <!-- Upload Zone -->
    <div class="upload-zone">
      <div class="upload-box" @click="triggerFileInput">
        <div class="upload-icon">
          <Upload />
        </div>
        <p>Upload image or video</p>
        <small>Supported: JPG, PNG, MP4, MOV</small>
      </div>

      <input type="file" ref="mediaInput" style="display: none" accept="image/jpeg,image/png,video/mp4,video/mov"
        @change="handleFileUpload" />

      <div class="uploading" v-if="mediaFile">
        <label>Uploaded - 1/1 file</label>
        <div class="boxxb">
          <div class="d-flex align-center justify-space-between">
            <div class="file-name">{{ mediaFile.name }}</div>
            <span class="remove-icon" @click="clearUpload">✕</span>
          </div>
          <div class="progress" v-if="uploading">
            <div class="bar" :style="{ width: progress + '%' }"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="mb-4">
      <p v-if="mediaError" class="error-msg">{{ mediaError }}</p>
    </div>
    <!-- Audio Upload Box (horizontal layout) -->
    <div class="form-group audio-upload-row" v-if="store.adCreativeData.isImage && hasTikTok">
      <div class="audio-label">
        <label class="tit">2–Upload audio <span class="required">*</span></label>
        <p class="subtext">
          *This audio will be used as the base sound for your TikTok ad.
        </p>
      </div>

      <div class="audio-drop-box" @click="$refs.audioInput.click()">
        <div class="content">
          <Sound class="icon" />
          <span>Drop your audio here to <a>upload</a></span>
        </div>
        <input type="file" ref="audioInput" accept="audio/*" style="display: none" @change="handleAudioUpload" />
      </div>
    </div>

    <div class="form-group">
      <label class="tit">Headline <span class="required">*</span></label>
      <input type="text" v-model="store.adCreativeData.headline" placeholder="Write post headline" />
    </div>

    <div class="form-group">
      <label class="tit">Caption <span class="required">*</span></label>
      <input type="text" v-model="store.adCreativeData.caption" placeholder="Write post caption" />
    </div>
  </div>
</template>

<script>
import apiclient from "@/core/buildRequest";
import Upload from "../icons/Upload.vue";
import { useSuperAddStore } from "@/store/superAddStore";
import Sound from "../icons/Sound.vue";

export default {
  name: "AdCreativeBox",
  components: { Upload, Sound },
  data() {
    return {
      uploading: false,
      progress: 0,
      mediaFile: null,
      audioFile: null,
      isImage: false,
      mediaError: null,
      store: useSuperAddStore(),
    };
  },
  methods: {
    triggerFileInput() {
      this.$refs.mediaInput.click();
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      this.mediaError = null; // reset
      if (!file) return;

      const type = file.type;
      const isImage = type.startsWith("image/");
      const isVideo = type.startsWith("video/");
      const allowedAspect = 9 / 16;
      const aspectTolerance = 0.05;

      if (isImage) {
        const img = new Image();
        img.onload = () => {
          const aspect = img.width / img.height;
          if (Math.abs(aspect - allowedAspect) > aspectTolerance) {
            this.mediaError = "Image must have 9:16 aspect ratio.";
            return;
          }
          this.afterUploadSuccess(file, true);
        };
        img.src = URL.createObjectURL(file);
        this.store.format = "Single Image";
      } else if (isVideo) {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          const aspect = video.videoWidth / video.videoHeight;
          if (Math.abs(aspect - allowedAspect) > aspectTolerance) {
            this.mediaError = "Video must have 9:16 aspect ratio.";
            return;
          }
          this.afterUploadSuccess(file, false);
        };
        video.src = URL.createObjectURL(file);
      } else {
        this.mediaError = "Unsupported file type.";
      }
    },

    afterUploadSuccess(file, isImage) {
      this.store.adCreativeData.isImage = isImage;
      this.mediaFile = file;
      this.audioFile = null;
      this.store.adCreativeData.videoFile = file;

      this.uploading = true;
      this.progress = 0;
      const interval = setInterval(() => {
        if (this.progress >= 100) clearInterval(interval);
        else this.progress += 10;
      }, 300);
    },
    async handleAudioUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.audioFile = file;
      this.store.adCreativeData.audioFile = file;

      const accounts = JSON.parse(
        localStorage.getItem("superselected_accounts")
      );
      const advertiserId = accounts?.tiktok?.id;

      const formData = new FormData();
      formData.append("advertiser_id", advertiserId);
      formData.append("music_file", file);

      try {
        const response = await apiclient.post(
          "/super-ad/upload/music",
          formData
        );

        const res = response.data;

        // تحقق من وجود music_id
        const musicId = res?.data?.music_id;

        if (musicId) {
          this.store.adCreativeData.music_id = musicId;
          console.log("✅ Stored music_id:", musicId);
        } else {
          alert("Upload succeeded but music_id not found in response.");
        }
      } catch (error) {
        console.error("❌ Upload failed:", error);
        alert("Failed to upload audio. Please try again.");
      }
    },
    clearUpload() {
      this.mediaFile = null;
      this.audioFile = null;
      this.uploading = false;
      this.progress = 0;
      this.$refs.mediaInput.value = "";
      this.store.adCreativeData.videoFile = null;
      this.store.adCreativeData.audioFile = null;
    },
    validate() {
      const errors = [];
      const data = this.store.adCreativeData;

      if (!data.headline?.trim()) {
        errors.push("Headline is required");
      }
      if (!data.caption?.trim()) {
        errors.push("Caption is required");
      }
      if (!this.mediaFile) {
        errors.push("Media file is required");
      } else if (data.isImage && !this.audioFile && this.hasTikTok) {
        errors.push("Audio is required when uploading an image for TikTok.");
      }

      if (errors.length > 0) {
        // إما ترجع false أو ترجع errors حسب استخدامك
        errors.forEach((e) => console.warn("Validation error:", e));
        return false;
      }

      return true;
    },

    getData() {
      return {
        ...this.store.adCreativeData,
        isImage: this.isImage,
      };
    },
  },
  computed: {
    hasTikTok() {
      try {
        const raw = localStorage.getItem("super_selected_platforms");
        if (!raw) return false;

        const platforms = JSON.parse(raw);
        return platforms.some(
          (p) => p.name && p.name.toLowerCase() === "tiktok"
        );
      } catch (e) {
        console.error("❌ Failed to parse super_selected_platforms:", e);
        return false;
      }
    }
  }
  ,
  mounted() {
    this.mediaFile = this.store.adCreativeData.videoFile;
    this.audioFile = this.store.adCreativeData.audioFile;
  },
};
</script>

<style scoped>
.box {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  flex: 1;
  min-width: 400px;
}

h3 {
  margin-bottom: 1.5rem;
  font-size: 18px;
  font-weight: 600;
}

.upload-zone {
  margin-bottom: 1.5rem;
  cursor: pointer;
}

.upload-box {
  background: #f5f5f5;
  padding: 2rem;
  border: 2px dashed #c2c2c2;
  border-radius: 10px;
  text-align: center;
  color: #555;
}

.upload-box a {
  color: #207878;
  text-decoration: underline;
  font-weight: 500;
}

.upload-icon img {
  width: 40px;
  height: 40px;
  margin-bottom: 1rem;
}

.uploading {
  margin-top: 1rem;
  font-size: 14px;
  color: #333;
}

.file-name {
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.progress {
  background: #e0e0e0;
  height: 8px;
  border-radius: 5px;
  overflow: hidden;
}

.progress .bar {
  width: 100%;
  height: 100%;
  background: #22c55e;
}

.form-group {
  margin-bottom: 1.25rem;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
}

.tit {
  min-width: 14rem;
}

input[type="text"] {
  padding: 0.65rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  width: 100%;
}

.required {
  color: red;
}

.boxxb {
  border: 1px solid #ccc;
  padding: 0.3rem;
  width: 50%;
  border-radius: 0.2rem;
  margin-top: 0.5rem;
}

.file-name-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.remove-icon {
  cursor: pointer;
  font-size: 16px;
  color: #888;
  transition: color 0.2s;
  padding: 0 8px;
}

.remove-icon:hover {
  color: #f44336;
}

.audio-upload {
  margin-top: 24px;
}

.audio-upload .subtext {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.drop-audio-box {
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.2s;
}

.drop-audio-box:hover {
  border-color: #aaa;
}

.drop-audio-box .inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
}

.drop-audio-box .inner .icon {
  width: 22px;
  height: 22px;
  color: #0075ff;
}

.drop-audio-box a {
  color: #0075ff;
  text-decoration: underline;
}

.uploaded-audio {
  margin-top: 8px;
  font-size: 13px;
  color: #222;
}

.audio-upload-row {
  display: block;
  width: 100%;
  gap: 16px;
  margin-top: 24px;
}

.audio-label {
  flex: 1;
}

.audio-label .tit {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 2px;
  display: inline-block;
}

.audio-label .subtext {
  font-size: 13px;
  color: #777;
}

.audio-drop-box {
  flex: 1;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  padding: 10px 16px;
  cursor: pointer;
  transition: border-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  margin-top: 0.6rem;
}

.audio-drop-box:hover {
  border-color: #999;
}

.audio-drop-box .content {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
}

.audio-drop-box .icon {
  width: 20px;
  height: 20px;
  color: #0075ff;
}

.audio-drop-box a {
  color: #0075ff;
  text-decoration: underline;
}

.error-msg {
  color: #e53935;
  font-size: 13px;
  margin-top: 6px;
}
</style>
